import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Sparkles, Hammer, FileText } from "lucide-react";
import api from "@/lib/api";
import { resolveAssetUrl, resolveContentHtml, extractFirstImageUrl } from "@/lib/url";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LikeButton from "@/components/LikeButton";
import Comments from "@/components/Comments";
import useSeo from "@/hooks/useSeo";

const META = {
    aktualnosci: { label: "Aktualności", Icon: Sparkles, cls: "bg-emerald-100 text-emerald-800" },
    wydarzenia: { label: "Wydarzenia", Icon: Calendar, cls: "bg-amber-100 text-amber-800" },
    remonty: { label: "Remonty", Icon: Hammer, cls: "bg-orange-100 text-orange-800" },
    organizacyjne: { label: "Organizacyjne", Icon: FileText, cls: "bg-blue-100 text-blue-800" },
};

const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" }); }
    catch { return ""; }
};

export default function WiadomoscDetailPage() {
    const { id } = useParams();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        api.get(`/news/${id}`)
            .then((r) => setItem(r.data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
        if (typeof window !== "undefined" && window.localStorage.getItem("admin_token")) {
            api.get("/auth/me")
                .then((r) => setIsAdmin(r.data?.role === "admin" || !!r.data?.permissions?.manage_news))
                .catch(() => {});
        }
    }, [id]);

    const meta = item ? (META[item.category] || META.aktualnosci) : META.aktualnosci;
    const Icon = meta.Icon;

    const previewText = item ? (item.content || "").replace(/<[^>]*>/g, "").slice(0, 160) : "";
    const ogImage = item ? (item.image_url ? resolveAssetUrl(item.image_url) : extractFirstImageUrl(item.content || "")) : undefined;
    useSeo({
        title: item?.title,
        description: previewText,
        image: ogImage || undefined,
        type: "article",
    });

    return (
        <div data-testid="wiadomosc-detail-page" className="min-h-screen bg-[#FDFDFD]">
            <Navbar />
            <header className="bg-[#092E22] text-white pt-32 pb-16">
                <div className="max-w-4xl mx-auto px-6 lg:px-10">
                    <Link to="/#wiadomosci" data-testid="back-to-wiadomosci" className="inline-flex items-center gap-2 text-[#D4A537] text-sm hover:underline mb-6">
                        <ArrowLeft size={14} /> Powrót do wiadomości
                    </Link>
                    {loading && <p className="text-white/70">Wczytywanie...</p>}
                    {notFound && <p className="text-white/70">Nie znaleziono wiadomości.</p>}
                    {item && (
                        <>
                            <div className="flex items-center gap-3 mb-4 flex-wrap">
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${meta.cls} text-xs font-semibold`}>
                                    <Icon size={12} /> {meta.label}
                                </span>
                                <span className="inline-flex items-center gap-1.5 text-white/70 text-xs">
                                    <Calendar size={12} /> {formatDate(item.created_at)}
                                </span>
                            </div>
                            <h1 className="font-serif text-4xl md:text-5xl leading-tight">{item.title}</h1>
                        </>
                    )}
                </div>
            </header>

            {item && (
                <main className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
                    {item.image_url && (
                        <div className="relative rounded-3xl overflow-hidden mb-10 aspect-[16/9]" style={{ maxHeight: 600 }}>
                            <img src={resolveAssetUrl(item.image_url)} alt={item.title} className="w-full h-full object-cover news-image-cap" />
                        </div>
                    )}
                    <motion.article
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 md:p-12"
                        data-testid="wiadomosc-content"
                    >
                        <div className="rich-content text-gray-700 leading-relaxed text-base md:text-lg" dangerouslySetInnerHTML={{ __html: resolveContentHtml(item.content) || "" }} />
                    </motion.article>

                    <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
                        <p className="text-xs text-gray-500">Spodobała Ci się ta wiadomość? Daj znać sąsiadom.</p>
                        <LikeButton newsId={item.id} />
                    </div>

                    <div className="mt-12">
                        <Comments newsId={item.id} isAdmin={isAdmin} />
                    </div>
                </main>
            )}

            <Footer />
        </div>
    );
}
